'use client';
import React from 'react';
import { Button, buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { MenuToggleIcon } from '@/components/ui/menu-toggle-icon';
import { useScroll } from '@/components/ui/use-scroll';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { ArrowRight } from 'lucide-react';

export const Header = () => {
  const [open, setOpen] = React.useState(false);
  const scrolled = useScroll(10);
  const buttonRef = React.useRef<HTMLButtonElement>(null);

  const links = [
    { label: 'Services', href: '#services' },
    { label: 'How It Works', href: '#process' },
    { label: 'Integrations', href: '#integrations' },
    { label: 'FAQ', href: '#faq' },
  ];

  React.useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const handleLinkClick = () => setOpen(false);

  return (
    <header
      className={cn(
        'sticky top-4 z-50 mx-auto w-full max-w-5xl md:rounded-2xl md:transition-all md:ease-out',
        {
          'glass md:top-4 md:max-w-4xl md:shadow-[0_8px_32px_hsl(var(--coral-bright)/0.06)]': scrolled && !open,
          'bg-transparent backdrop-blur-xl': open,
          'bg-transparent': !scrolled && !open,
        },
      )}
    >
      <nav className={cn('flex h-14 w-full items-center justify-between px-4 md:h-12 md:transition-all md:ease-out', { 'md:px-3': scrolled })}>
        <a href="/" className="flex items-center gap-2">
          <img src="/favicon.png" alt="Clawbiz" className="w-7 h-7 rounded-md" />
          <span className="font-display font-medium text-lg tracking-tight">Clawbiz</span>
        </a>
        <div className="hidden items-center gap-2 md:flex">
          {links.map((link, i) => (
            <a key={i} className={cn(buttonVariants({ variant: 'ghost' }), 'text-foreground/70 hover:text-foreground hover:bg-white/5')} href={link.href}>{link.label}</a>
          ))}
          <Button size="sm" className="glow-primary" asChild>
            <a href="#contact">
              Get Set Up
              <ArrowRight className="w-4 h-4" />
            </a>
          </Button>
        </div>
        <DropdownMenu open={open} onOpenChange={setOpen}>
          <DropdownMenuTrigger asChild>
            <Button ref={buttonRef} size="icon" variant="ghost" className="md:hidden relative" aria-label={open ? 'Close menu' : 'Open menu'} aria-expanded={open}>
              <MenuToggleIcon open={open} className="size-5" duration={300} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" side="bottom" alignOffset={-16} sideOffset={0} className="md:hidden !w-screen !max-w-none !border-0 glass !rounded-none rounded-t-lg p-4 !right-0 !left-auto !translate-x-0">
            <div className="flex flex-col gap-1">
              {links.map((link) => (
                <DropdownMenuItem key={link.label} asChild>
                  <a href={link.href} className="flex items-center px-3 py-2.5 text-base rounded-md hover:text-primary cursor-pointer" onClick={handleLinkClick}>{link.label}</a>
                </DropdownMenuItem>
              ))}
            </div>
            <DropdownMenuSeparator className="my-2 border-0 bg-transparent h-px opacity-0" />
            <div className="flex flex-col gap-2 px-1 pb-1">
              <Button className="w-full justify-start glow-primary" asChild>
                <a href="#contact" onClick={handleLinkClick}>
                  Get Set Up
                  <ArrowRight className="w-4 h-4" />
                </a>
              </Button>
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      </nav>
    </header>
  );
};
